// Comprobante de Aguinaldo (anual) — formato inspirado en el ejemplo provisto por el usuario.
// Paleta neutra: gris-azul sobrio (no replicamos el azul corporativo del ejemplo).
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { DEJAVU_REGULAR_BASE64, DEJAVU_BOLD_BASE64 } from "@/lib/boletaFont";

const FONT_FAMILY = "NomyProSans";

// Paleta
const COLOR_TITLE_BG = [220, 228, 235];     // azul muy claro (banda título)
const COLOR_LABEL_BG = [232, 238, 244];
const COLOR_VALUE_BG = [247, 250, 253];
const COLOR_TEXT = [30, 38, 45];
const COLOR_MUTED = [120, 130, 140];
const COLOR_BORDER = [180, 190, 200];
const COLOR_ACCENT = [70, 100, 130];

const fmtCRC = (n) => "₡" + new Intl.NumberFormat("es-CR", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(Number(n) || 0);

function registerFont(doc) {
  doc.addFileToVFS("NomyProSans-Regular.ttf", DEJAVU_REGULAR_BASE64);
  doc.addFont("NomyProSans-Regular.ttf", FONT_FAMILY, "normal");
  doc.addFileToVFS("NomyProSans-Bold.ttf", DEJAVU_BOLD_BASE64);
  doc.addFont("NomyProSans-Bold.ttf", FONT_FAMILY, "bold");
}

function detectImageFormat(dataUrl) {
  if (!dataUrl || typeof dataUrl !== "string") return null;
  const m = dataUrl.match(/^data:image\/(png|jpeg|jpg|webp);base64,/i);
  if (!m) return null;
  return m[1].toLowerCase() === "jpg" ? "JPEG" : m[1].toUpperCase();
}

export function generarComprobanteAguinaldo({ empresa, empleado, year, desglose, aguinaldo }) {
  const doc = new jsPDF({ unit: "mm", format: "letter" });
  registerFont(doc);
  doc.setFont(FONT_FAMILY, "normal");
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  let y = margin;

  // === CABECERA EMPRESA ===
  doc.setFillColor(...COLOR_TITLE_BG);
  doc.setDrawColor(...COLOR_BORDER);
  doc.setLineWidth(0.3);
  doc.rect(margin, y, pageW - margin * 2, 14, "FD");
  doc.setTextColor(...COLOR_TEXT);
  doc.setFont(FONT_FAMILY, "bold");
  doc.setFontSize(15);
  const empresaNombre = empresa?.razon_social || empresa?.nombre || "Empresa";
  doc.text(empresaNombre, pageW / 2, y + 9, { align: "center" });
  y += 16;

  // === Título + Período ===
  doc.setFont(FONT_FAMILY, "normal");
  doc.setFontSize(10);
  doc.text("COMPROBANTE DE PAGO", margin, y + 4);
  y += 9;
  doc.text("PERIODO DE PAGO:", margin, y + 4);
  doc.setFillColor(...COLOR_LABEL_BG);
  doc.rect(margin + 40, y, 90, 6, "FD");
  doc.setFont(FONT_FAMILY, "bold");
  doc.setFontSize(11);
  doc.text(`Aguinaldo ${year}`, margin + 40 + 45, y + 4.5, { align: "center" });
  y += 11;

  // === Logo a la derecha ===
  const logoFmt = detectImageFormat(empresa?.logo);
  if (logoFmt) {
    try {
      doc.addImage(empresa.logo, logoFmt, pageW - margin - 38, margin + 18, 36, 50);
    } catch (e) { /* ignore */ }
  }

  // === Datos empleado (lado izquierdo) ===
  const rowH = 7;
  const labelW = 42;
  const valueW = 75;
  const empRows = [
    ["Trabajador:",         `${empleado.nombre || ""} ${empleado.apellidos || ""}`.trim()],
    ["Cédula de Identidad:", empleado.cedula || "—"],
    ["Número Celular:",      empleado.telefono || empleado.celular || "—"],
    ["Correo Electrónico:",  empleado.correo || empleado.email || "—"],
    ["Puesto:",              empleado.puesto || "—"],
  ];
  empRows.forEach((r, i) => {
    const yy = y + i * rowH;
    doc.setFillColor(...COLOR_LABEL_BG); doc.rect(margin, yy, labelW, rowH, "FD");
    doc.setFillColor(...COLOR_VALUE_BG); doc.rect(margin + labelW, yy, valueW, rowH, "FD");
    doc.setFont(FONT_FAMILY, "bold"); doc.setFontSize(9); doc.setTextColor(...COLOR_TEXT);
    doc.text(r[0], margin + 1.5, yy + 4.7);
    doc.setFont(FONT_FAMILY, "normal");
    doc.text(String(r[1] || "—"), margin + labelW + 2, yy + 4.7);
  });
  y += empRows.length * rowH + 8;

  // === Tabla Año | Mes | Salario ===
  const body = desglose.map(d => [String(d.anio), d.label, fmtCRC(d.salario)]);
  autoTable(doc, {
    startY: y,
    margin: { left: margin },
    tableWidth: labelW + valueW,
    head: [["Año", "Mes", "Salario"]],
    body,
    theme: "grid",
    styles: { font: FONT_FAMILY, fontSize: 9, cellPadding: 1.6, textColor: COLOR_TEXT, lineColor: COLOR_BORDER, lineWidth: 0.15 },
    headStyles: { fillColor: COLOR_LABEL_BG, textColor: COLOR_TEXT, fontStyle: "bold", halign: "center" },
    columnStyles: {
      0: { halign: "center", cellWidth: 16, fillColor: [255, 255, 255] },
      1: { halign: "left",   cellWidth: 32, fillColor: COLOR_LABEL_BG },
      2: { halign: "right",  cellWidth: 36, fillColor: COLOR_VALUE_BG },
    },
  });
  const tablaFinalY = doc.lastAutoTable.finalY;

  // === Card "Aguinaldo a Recibir" (lado derecho, alineado con la tabla) ===
  const cardX = margin + labelW + valueW + 20;
  const cardW = pageW - margin - cardX;
  const cardY = y + 24;
  doc.setFillColor(...COLOR_LABEL_BG);
  doc.setDrawColor(...COLOR_BORDER);
  doc.rect(cardX, cardY, cardW, 10, "FD");
  doc.setTextColor(...COLOR_TEXT);
  doc.setFont(FONT_FAMILY, "bold"); doc.setFontSize(11);
  doc.text("Aguinaldo a Recibir:", cardX + cardW / 2, cardY + 6.5, { align: "center" });
  doc.setFillColor(...COLOR_VALUE_BG);
  doc.rect(cardX, cardY + 10, cardW, 14, "FD");
  doc.setTextColor(...COLOR_ACCENT);
  doc.setFontSize(16);
  doc.text(fmtCRC(aguinaldo), cardX + cardW / 2, cardY + 19.5, { align: "center" });

  // === Firmas ===
  let firmasY = Math.max(tablaFinalY + 25, pageH - 35);
  if (firmasY + 18 > pageH - margin) { doc.addPage(); firmasY = pageH - 50; }
  doc.setDrawColor(...COLOR_BORDER); doc.setLineWidth(0.4);
  const firmaW = 70;
  const f1X = pageW * 0.27 - firmaW / 2;
  const f2X = pageW * 0.73 - firmaW / 2;
  doc.line(f1X, firmasY, f1X + firmaW, firmasY);
  doc.line(f2X, firmasY, f2X + firmaW, firmasY);
  doc.setTextColor(...COLOR_TEXT);
  doc.setFont(FONT_FAMILY, "bold"); doc.setFontSize(9);
  doc.text(empresaNombre, pageW * 0.27, firmasY + 4.5, { align: "center" });
  doc.text(`${empleado.nombre || ""} ${empleado.apellidos || ""}`.trim() || "—", pageW * 0.73, firmasY + 4.5, { align: "center" });
  doc.setFont(FONT_FAMILY, "normal"); doc.setFontSize(7.5); doc.setTextColor(...COLOR_MUTED);
  doc.text("Firma del Encargado Patronal", pageW * 0.27, firmasY + 8.5, { align: "center" });
  doc.text("Firma del Trabajador",         pageW * 0.73, firmasY + 8.5, { align: "center" });

  // Pie
  doc.setFont(FONT_FAMILY, "normal"); doc.setFontSize(7); doc.setTextColor(...COLOR_MUTED);
  const fechaGen = new Date().toLocaleDateString("es-CR", { day: "2-digit", month: "long", year: "numeric" });
  doc.text(`Generado el ${fechaGen} · ${empresaNombre} · Aguinaldo ${year}`, pageW - margin, pageH - 6, { align: "right" });

  const safe = `${empleado.nombre || ""}_${empleado.apellidos || ""}`.replace(/\s+/g, "_");
  const fileName = `Aguinaldo_${year}_${safe}.pdf`;
  doc.save(fileName);
  const dataUri = doc.output("datauristring");
  return { fileName, pdfBase64: dataUri.split(",")[1] };
}
